import { createContext, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { speak, chime, timeOfDayGreeting } from "@/lib/voice";
import { toast } from "sonner";

const AuthCtx = createContext(null);

// 12 hours of inactivity = auto logout
const IDLE_TIMEOUT_MS = 12 * 60 * 60 * 1000;
const ACTIVITY_KEY = "wh_last_activity";
// Throttle activity writes to localStorage (don't write on every mousemove)
const ACTIVITY_WRITE_INTERVAL_MS = 30 * 1000;

// Decode JWT payload without verifying signature — used only to read `exp` for auto-logout timing.
function decodeJwtExp(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const expiryTimerRef = useRef(null);
  const idleTimerRef = useRef(null);
  const lastWriteRef = useRef(0);

  const clearAllTimers = () => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  };

  const doLogout = (reason) => {
    clearAllTimers();
    localStorage.removeItem("wh_token");
    localStorage.removeItem("wh_user");
    localStorage.removeItem(ACTIVITY_KEY);
    setUser(null);
    if (reason === "expired") {
      try { toast.error("Session expired — please login again"); } catch {}
    } else if (reason === "idle") {
      try { toast.error("Logged out — 12 hours of inactivity"); } catch {}
    }
    window.location.replace("/login");
  };

  const scheduleAutoLogout = (token) => {
    if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    const expMs = decodeJwtExp(token);
    if (!expMs) return;
    const delay = expMs - Date.now();
    if (delay <= 0) {
      doLogout("expired");
      return;
    }
    // JavaScript's setTimeout takes a 32-bit signed int (max ~24.8 days
    // / 2147483647 ms). Passing a larger value triggers an integer
    // overflow which fires the callback IMMEDIATELY — the exact bug
    // that bounced users back to /login right after OTP verify when the
    // backend started issuing 30-day JWTs (per 2026-02-11 user report).
    // Cap the timer at 24 days; the next API call after that will re-
    // evaluate the JWT exp + inactivity window server-side anyway.
    const MAX_SETTIMEOUT = 24 * 24 * 60 * 60 * 1000; // 24 days
    const safeDelay = Math.min(delay, MAX_SETTIMEOUT);
    expiryTimerRef.current = setTimeout(() => doLogout("expired"), safeDelay);
  };

  const resetIdleTimer = () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => doLogout("idle"), IDLE_TIMEOUT_MS);
  };

  // Activity bump — record timestamp + reset idle timer. Throttled.
  const bumpActivity = () => {
    const now = Date.now();
    if (now - lastWriteRef.current >= ACTIVITY_WRITE_INTERVAL_MS) {
      lastWriteRef.current = now;
      try {
        localStorage.setItem(ACTIVITY_KEY, String(now));
      } catch {}
    }
    resetIdleTimer();
  };

  // Bind activity listeners once user is logged in
  useEffect(() => {
    if (!user) return undefined;
    const events = ["mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((e) => window.addEventListener(e, bumpActivity, { passive: true }));
    // Also react to cross-tab activity via storage events (other tab being used)
    const onStorage = (ev) => {
      if (ev.key === ACTIVITY_KEY) resetIdleTimer();
    };
    window.addEventListener("storage", onStorage);
    return () => {
      events.forEach((e) => window.removeEventListener(e, bumpActivity));
      window.removeEventListener("storage", onStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const token = localStorage.getItem("wh_token");
    if (!token) {
      setLoading(false);
      return;
    }
    // If token already expired, logout immediately
    const expMs = decodeJwtExp(token);
    if (expMs && expMs <= Date.now()) {
      doLogout("expired");
      setLoading(false);
      return;
    }
    // If last activity was > 12 hours ago, logout (covers tab-closed-then-reopened)
    const lastActivity = parseInt(localStorage.getItem(ACTIVITY_KEY) || "0", 10);
    if (lastActivity && Date.now() - lastActivity > IDLE_TIMEOUT_MS) {
      doLogout("idle");
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        scheduleAutoLogout(token);
        bumpActivity();
      })
      .catch(() => {
        localStorage.removeItem("wh_token");
        localStorage.removeItem("wh_user");
      })
      .finally(() => setLoading(false));
    return clearAllTimers;
  }, []);

  // First-load welcome announcement. Runs once when `user` becomes non-null.
  // Most browsers allow speechSynthesis after the user has just navigated
  // through a click flow (login form, OTP). If the autoplay policy blocks the
  // first call, the browser silently no-ops — no error to the user.
  const welcomeFiredRef = useRef(false);
  useEffect(() => {
    if (!user || welcomeFiredRef.current) return;
    welcomeFiredRef.current = true;
    try {
      const firstName = (user.full_name || "").trim().split(/\s+/)[0] || "there";
      const phrase = `Hello ${firstName}, ${timeOfDayGreeting()}! Welcome to RodBez WhatsApp.`;
      // Defer to next tick so React commit phase finishes before the browser
      // executes audio (some browsers gate audio while React is rendering).
      setTimeout(() => {
        chime(660, 250, 0.1);
        setTimeout(() => speak(phrase), 250);
      }, 0);
    } catch (_) {}
  }, [user]);

  // verifyOtp sets welcomeFiredRef so the gesture-listener doesn't double-speak
  // (handled in verifyOtp below).

  const requestOtp = async (phone) => {
    const cleanPhone = (phone || "").replace(/\D/g, "");
    const { data } = await api.post("/auth/request-otp", { phone: cleanPhone });
    return data;
  };

  const verifyOtp = async (phone, otp) => {
    const cleanPhone = (phone || "").replace(/\D/g, "");
    const { data } = await api.post("/auth/verify-otp", { phone: cleanPhone, otp });
    localStorage.setItem("wh_token", data.token);
    localStorage.setItem("wh_user", JSON.stringify(data.user));
    setUser(data.user);
    scheduleAutoLogout(data.token);
    bumpActivity();
    // Welcome announcement (also unlocks browser autoplay policy for later TTS)
    welcomeFiredRef.current = true;
    try {
      const firstName = (data.user.full_name || "").trim().split(/\s+/)[0] || "there";
      const phrase = `Hello ${firstName}, ${timeOfDayGreeting()}! Welcome to RodBez WhatsApp.`;
      chime(660, 250, 0.1);
      setTimeout(() => speak(phrase), 250);
    } catch (_) {}
    return data.user;
  };

  // Email + password login — used by admins and the Meta App Reviewer.
  // The OTP flow remains the default for regular users.
  const loginWithPassword = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("wh_token", data.token);
    localStorage.setItem("wh_user", JSON.stringify(data.user));
    setUser(data.user);
    scheduleAutoLogout(data.token);
    bumpActivity();
    welcomeFiredRef.current = true;
    return data.user;
  };

  const logout = () => doLogout("manual");

  return (
    <AuthCtx.Provider value={{ user, loading, requestOtp, verifyOtp, loginWithPassword, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
